import { FC, useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import AccountTreeIcon from "@mui/icons-material/AccountTree";
import CreateNewFolderIcon from "@mui/icons-material/CreateNewFolder";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import RemoveModeratorOutlinedIcon from "@mui/icons-material/RemoveModeratorOutlined";
import SaveIcon from "@mui/icons-material/Save";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import WorkHistoryIcon from "@mui/icons-material/WorkHistory";
import {
	Badge,
	Box,
	Button,
	Divider,
	ListItemIcon,
	ListItemText,
	MenuItem,
	Tooltip,
	Typography,
} from "@mui/material";

import CloseFolderIcon from "@/assets/svg/interactions/folder-close.svg?react";
import OpenFolderIcon from "@/assets/svg/interactions/folder-open.svg?react";

import { AdminProtected } from "@/components/Application/AdminProtected";
import { Translate } from "@/components/Application/Translate";
import { EditProcedureModal } from "@/components/Procedures/EditProcedure";
import { CreateNewProcedureNodeModal } from "@/components/Procedures/NewNode/CreateNewProcedureNode";
import { NewProcedureUpdateNotice } from "@/components/Procedures/NewProcedureUpdateNotice";
import { ProcedureKnowledgeModal } from "@/components/Procedures/ProcedureKnowledgeModal";

import { AlertRequest } from "@/helpers/alerts/AlertRequest";
import AnimatedBadge from "@/helpers/components/AnimatedBadge";
import { StyledMenu } from "@/helpers/components/StyledMenu";

import { Procedure } from "@/model/Procedure/Procedure";
import { User } from "@/model/User/User";
import { UserContext } from "@/model/User/UserContext";

import { checkAdminAccess } from "@/service/ApplicationQueries";
import { useMutateProcedureKnowledgeStatus } from "@/service/ProcedureMutations";
import { useFetchActiveProcedureDetails } from "@/service/ProcedureQueries";
import { useFetchProceduresSettings } from "@/service/Settings/SettingsQueries";
import { RouteParams } from "@/service/Utilities";

interface ProcedureActionProps {
	procedure: Procedure | undefined;
	nodeStepIds: number[] | undefined;
	CloseAllOpenDetailNodes: () => void;
	OpenAllOpenDetailNodes: (detailNodeIds: Array<number> | undefined) => void;
	toggleHistory: (open: boolean) => void;
	toggleComplete: (open: boolean) => void;
	setActiveSchemeId: (schemeId: number) => void;
	setOpenDetailNodes: (nodeId: Set<number>) => void;
	setEditChecklist?: (open: boolean) => void; // kept from your original signature
}

export const ProcedureActions: FC<ProcedureActionProps> = (props) => {
	const {
		nodeStepIds,
		CloseAllOpenDetailNodes,
		OpenAllOpenDetailNodes,
		toggleHistory,
		toggleComplete,
	} = props;

	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const [editProcedureModal, setEditProcedureModal] = useState(false);
	const [newNodeModal, setNewNodeModal] = useState(false);
	const [updateNoticeModal, setUpdateNoticeModal] = useState(false);
	const [knowledgeModalOpen, setKnowledgeModalOpen] = useState(false);
	const [isKnowledgeConfirmed, setIsKnowledgeConfirmed] = useState(false);

	const user = useContext(UserContext);
	const isAdmin = checkAdminAccess(user as User);

	const { data: procedureDetails } = useFetchActiveProcedureDetails();
	const { data: procedureSettings } = useFetchProceduresSettings();

	const unLockedNodes = procedureDetails?.ProcedureDetailNodes?.filter(
		(n) => n.CompletedState !== "Locked",
	);

	const allNodesDone =
		!!unLockedNodes?.length &&
		unLockedNodes.every((n) => n.CompletedState === "Done");

	const disableProcedureUpdates =
		!!(
			procedureDetails?.ParrentProcedureId &&
			procedureDetails?.ParrentProcedureId > 0
		) && Boolean(procedureDetails?.DisableProcedureUpdates);

	const areAllStepsUnchecked =
		!procedureDetails?.ProcedureDetailNodes?.some((x) => x.Done > 0) ?? false;

	const disableCompleteButton =
		(!isAdmin &&
			procedureSettings?.StrictChecklistCompletion &&
			!allNodesDone) ||
		!!(
			procedureDetails?.ParrentProcedureId &&
			procedureDetails?.ParrentProcedureId > 0
		) ||
		areAllStepsUnchecked;

	const { id } = useParams<RouteParams>();
	const navigate = useNavigate();

	const open = Boolean(anchorEl);
	const handleButtonClick = (event: React.MouseEvent<HTMLElement>) => {
		setAnchorEl(event.currentTarget);
	};
	const handleClose = () => setAnchorEl(null);

	// knowledge confirmation flag (local storage mirror)
	useEffect(() => {
		const key = `procedureKnowledgeConfirmed_${id}`;
		setIsKnowledgeConfirmed(Boolean(localStorage.getItem(key)));
	}, [id]);

	const sendProcedureKnowledgeStatus = useMutateProcedureKnowledgeStatus();
	const handleConfirmProcedureKnowledge = async () => {
		try {
			await sendProcedureKnowledgeStatus({ ProcedureId: id, Status: true });
			setKnowledgeModalOpen(false);
			AlertRequest.success();
			if (id) localStorage.setItem(`procedureKnowledgeConfirmed_${id}`, "1");
			setIsKnowledgeConfirmed(true);
		} catch (error) {
			AlertRequest.failed();
			console.error(error);
		}
	};

	return (
		<>
			<Tooltip
				title={
					<Translate String="Procedures.Actions.Tooltip">
						Manage procedure, nodes and completion
					</Translate>
				}
				enterDelay={600}
			>
				<Box>
					<Button
						sx={{ borderRadius: 2, textTransform: "none", fontWeight: 800 }}
						id="procedure-actions-button"
						aria-controls={open ? "procedure-actions-menu" : undefined}
						aria-haspopup="menu"
						aria-expanded={open ? "true" : undefined}
						variant="contained"
						color="info"
						disableElevation
						onClick={handleButtonClick}
						endIcon={<KeyboardArrowDownIcon />}
					>
						<Translate String="Procedures.Actions">Actions</Translate>
					</Button>
				</Box>
			</Tooltip>

			<AnimatedBadge
				badgeContent={"!"}
				sx={{ marginTop: "-20px" }}
				color="warning"
				invisible={!allNodesDone}
				anchorOrigin={{ vertical: "top", horizontal: "right" }}
			/>

			<StyledMenu
				id="procedure-actions-menu"
				MenuListProps={{
					"aria-labelledby": "procedure-actions-button",
					dense: true,
				}}
				anchorEl={anchorEl}
				open={open}
				onClose={handleClose}
				keepMounted
				anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
				transformOrigin={{ vertical: "top", horizontal: "left" }}
			>
				<Box px={1.5} pt={1} pb={0.5}>
					<Typography
						variant="overline"
						color="text.secondary"
						sx={{ letterSpacing: 0.6 }}
					>
						<Translate String="Procedures.Actions.Section.Manage">
							Manage
						</Translate>
					</Typography>
				</Box>

				<AdminProtected>
					<MenuItem
						onClick={() => {
							setEditProcedureModal(true);
							handleClose();
						}}
					>
						<ListItemIcon>
							<CreateNewFolderIcon color="primary" fontSize="small" />
						</ListItemIcon>
						<ListItemText
							primary={
								<Translate
									HTMLTag="span"
									String="Procedures.EditProcedure.Header"
								>
									Edit Procedure
								</Translate>
							}
							secondary={
								<Typography variant="caption" color="text.secondary">
									<Translate String="Procedures.EditProcedure.Desc">
										Change name, metadata, or structure
									</Translate>
								</Typography>
							}
						/>
					</MenuItem>

					<MenuItem
						onClick={() => {
							setNewNodeModal(true);
							handleClose();
						}}
					>
						<ListItemIcon>
							<PlaylistAddIcon color="info" fontSize="small" />
						</ListItemIcon>
						<ListItemText
							primary={
								<Translate HTMLTag="span" String="Procedure.NewMainNode">
									New Node
								</Translate>
							}
							secondary={
								<Typography variant="caption" color="text.secondary">
									<Translate String="Procedure.NewMainNode.Desc">
										Add a top-level node to this procedure
									</Translate>
								</Typography>
							}
						/>
					</MenuItem>

					<MenuItem
						onClick={() => {
							setUpdateNoticeModal(true);
							handleClose();
						}}
						disabled={disableProcedureUpdates}
					>
						<ListItemIcon>
							<PlaylistAddIcon fontSize="small" />
						</ListItemIcon>
						<ListItemText
							primary={
								<Translate HTMLTag="span" String="Procedure.New.Update.Notice">
									New Procedure update notice
								</Translate>
							}
							secondary={
								<Typography variant="caption" color="text.secondary">
									<Translate String="Procedure.New.Update.Notice.Desc">
										Notify users about important changes
									</Translate>
								</Typography>
							}
						/>
					</MenuItem>

					<Divider />
				</AdminProtected>

				<Box px={1.5} pt={1} pb={0.5}>
					<Typography
						variant="overline"
						color="text.secondary"
						sx={{ letterSpacing: 0.6 }}
					>
						<Translate String="Procedures.Actions.Section.View">View</Translate>
					</Typography>
				</Box>

				<MenuItem
					onClick={() => {
						toggleHistory(true);
						handleClose();
					}}
				>
					<ListItemIcon>
						<WorkHistoryIcon fontSize="small" />
					</ListItemIcon>
					<ListItemText
						primary={
							<Translate HTMLTag="span" String="Procedure.Actions.ShowHistory">
								Show history
							</Translate>
						}
						secondary={
							<Typography variant="caption" color="text.secondary">
								<Translate String="Procedure.Actions.ShowHistory.Desc">
									View activity log and changes
								</Translate>
							</Typography>
						}
					/>
				</MenuItem>

				<MenuItem
					onClick={() => {
						OpenAllOpenDetailNodes(nodeStepIds);
						handleClose();
					}}
				>
					<ListItemIcon>
						<OpenFolderIcon width="20" />
					</ListItemIcon>
					<ListItemText
						primary={
							<Translate HTMLTag="span" String="Procedure.Actions.ExpandAll">
								Expand all groups
							</Translate>
						}
					/>
				</MenuItem>

				<MenuItem
					onClick={() => {
						CloseAllOpenDetailNodes();
						handleClose();
					}}
				>
					<ListItemIcon>
						<CloseFolderIcon width="20" />
					</ListItemIcon>
					<ListItemText
						primary={
							<Translate HTMLTag="span" String="Procedure.Actions.CollapseAll">
								Collapse all groups
							</Translate>
						}
					/>
				</MenuItem>

				<AdminProtected>
					<MenuItem
						onClick={() => {
							navigate(`/procedure/${id}/nodeDiagram`);
							handleClose();
						}}
					>
						<ListItemIcon>
							<AccountTreeIcon fontSize="small" />
						</ListItemIcon>
						<ListItemText
							primary={
								<Translate
									HTMLTag="span"
									String="Procedure.Actions.ShowNodeChart"
								>
									Show all nodecharts
								</Translate>
							}
							secondary={
								<Typography variant="caption" color="text.secondary">
									<Translate String="Procedure.Actions.ShowNodeChart.Desc">
										Visualize relationships between nodes
									</Translate>
								</Typography>
							}
						/>
					</MenuItem>
				</AdminProtected>

				<Divider />

				<Box px={1.5} pt={1} pb={0.5}>
					<Typography
						variant="overline"
						color="text.secondary"
						sx={{ letterSpacing: 0.6 }}
					>
						<Translate String="Procedures.Actions.Section.Actions">
							Actions
						</Translate>
					</Typography>
				</Box>

				<MenuItem
					onClick={() => {
						setKnowledgeModalOpen(true);
						handleClose();
					}}
				>
					<ListItemIcon>
						{isKnowledgeConfirmed ? (
							<VerifiedUserOutlinedIcon color="success" fontSize="small" />
						) : (
							<RemoveModeratorOutlinedIcon color="warning" fontSize="small" />
						)}
					</ListItemIcon>
					<ListItemText
						primary={
							<Translate
								HTMLTag="span"
								String="Procedure.Actions.ProcedureKnowledge"
							>
								Procedure knowledge
							</Translate>
						}
						secondary={
							<Typography variant="caption" color="text.secondary">
								{isKnowledgeConfirmed ? (
									<Translate String="Procedure.Actions.ProcedureKnowledge.Confirmed">
										Confirmed for this procedure
									</Translate>
								) : (
									<Translate String="Procedure.Actions.ProcedureKnowledge.Request">
										Confirm your knowledge of this procedure
									</Translate>
								)}
							</Typography>
						}
					/>
				</MenuItem>

				<MenuItem
					onClick={() => {
						toggleComplete(true);
						handleClose();
					}}
					disabled={disableCompleteButton}
				>
					<ListItemIcon>
						<SaveIcon
							color={disableCompleteButton ? "disabled" : "success"}
							fontSize="small"
						/>
					</ListItemIcon>
					<ListItemText
						primary={
							<Badge variant="dot" color="warning" invisible={!allNodesDone}>
								<Translate
									HTMLTag="span"
									String="Procedure.SaveAndEdit.CompleteReset"
								>
									Complete
								</Translate>
							</Badge>
						}
						secondary={
							<Typography variant="caption" color="text.secondary">
								<Translate String="Procedure.Actions.Complete.Desc">
									Save and mark as complete
								</Translate>
							</Typography>
						}
					/>
				</MenuItem>
			</StyledMenu>

			{/* Modals */}
			<EditProcedureModal
				open={editProcedureModal}
				onClose={() => setEditProcedureModal(false)}
				id={Number(id)}
			/>
			<CreateNewProcedureNodeModal
				open={newNodeModal}
				onClose={() => setNewNodeModal(false)}
			/>
			<NewProcedureUpdateNotice
				open={updateNoticeModal}
				onClose={() => setUpdateNoticeModal(false)}
			/>
			<ProcedureKnowledgeModal
				open={knowledgeModalOpen}
				onClose={() => setKnowledgeModalOpen(false)}
				onConfirm={handleConfirmProcedureKnowledge}
			/>
		</>
	);
};
